import type { Action, Resource, SessionUserDto } from '@ukinfo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { api, ApiError, isNetworkError } from './api';
import { wipeOfflineData } from './offline-account';
import { disablePush } from './push';
import { redeemFromUrl } from './app-start';

interface AuthContextValue {
  user: SessionUserDto | null;
  isLoading: boolean;
  /** Die Sitzung liess sich nicht pruefen, weil der Server nicht erreichbar ist (kein Netz). */
  networkError: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  viewAs: (personId: string) => Promise<void>;
  stopViewingAs: () => Promise<void>;
  /**
   * Grobe Pruefung fuer die Anzeige von Bedienelementen.
   * Die verbindliche Pruefung erfolgt immer serverseitig.
   */
  can: (resource: Resource, action: Action) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      // Erster Start der App vom Home-Bildschirm: Anmeldung aus Safari uebernehmen.
      const transferred = await redeemFromUrl();
      if (transferred) return transferred;
      try {
        return await api.get<SessionUserDto>('/api/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const user = data ?? null;
  const networkError = !data && isNetworkError(error);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await api.post<SessionUserDto>('/api/auth/login', { email, password });
      queryClient.setQueryData(['auth', 'me'], session);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    // Wer sich abmeldet, will auf diesem Geraet keine Meldungen mehr - etwa
    // am gemeinsam genutzten Vereinsrechner.
    await disablePush('account').catch(() => undefined);
    // Ebenso die offline gespeicherten Einheiten: sie gehoeren zu diesem Konto auf diesem Geraet.
    await wipeOfflineData(user?.userId).catch(() => undefined);
    await api.post('/api/auth/logout');
    queryClient.setQueryData(['auth', 'me'], null);
    queryClient.clear();
  }, [queryClient, user?.userId]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient]);

  const replaceSessionUser = useCallback(
    (session: SessionUserDto) => {
      queryClient.clear();
      queryClient.setQueryData(['auth', 'me'], session);
    },
    [queryClient],
  );

  const viewAs = useCallback(
    async (personId: string) => {
      replaceSessionUser(await api.post<SessionUserDto>('/api/auth/view-as', { personId }));
    },
    [replaceSessionUser],
  );

  const stopViewingAs = useCallback(async () => {
    replaceSessionUser(await api.post<SessionUserDto>('/api/auth/view-as/stop'));
  }, [replaceSessionUser]);

  const can = useCallback(
    (resource: Resource, action: Action) => {
      if (!user) return false;
      if (user.isSuperadmin) return true;
      return user.permissions[`${resource}.${action}`] === true;
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, networkError, login, logout, refresh, viewAs, stopViewingAs, can }),
    [user, isLoading, networkError, login, logout, refresh, viewAs, stopViewingAs, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return context;
}
